import { z } from "zod";

/**
 * The HTTP contract every job-running agent implements. The orchestrator
 * dispatches jobs by POSTing an `AgentInput` to `agents.endpoint_url` and
 * the agent reports back by POSTing an `AgentOutput` to
 * `${PUBLIC_BASE_URL}/api/callbacks/:jobId`. Both directions are
 * provider-agnostic — the same shapes serve the DB Curator, DB Validator,
 * and any future job-running agent.
 *
 * Lives in `@prospect/shared` so the orchestrator and every agent service
 * import from one source of truth — no redeclaration on either side.
 *
 * The chatbot (M2) is **not** a job-running agent and uses a different
 * transport entirely; these schemas do not apply to it.
 */

// ─── Input (orchestrator → agent) ──────────────────────────────────────────

export const agentInputSchema = z.object({
  /** UUID v7 of the `jobs` row. The agent echoes this back in its callback. */
  jobId: z.string().uuid(),
  /** Vocabulary value matching the active `agents.agent_type`. */
  agentType: z.string().min(1),
  /**
   * Frozen copy of `agent_config` at dispatch time. Treated as immutable by
   * the agent — re-reads of the live config during a run break
   * reproducibility (decision 2 in `2026-04-21-m1-db-schema.md`). The
   * orchestrator populates this from `jobs.config_snapshot`, which is
   * frozen at dispatch via `readAgentConfigSnapshot` against
   * `agent_config!A:B`. Empty (`{}`) only when dispatch ran without a
   * sheets client wired (in-process harnesses). Agents still re-parse
   * with their own Zod for type coercion + defaults gap-fill — that
   * post-Zod copy is what surfaces in their artifact's
   * `agentConfigSnapshot`.
   */
  config: z.record(z.unknown()),
  /** Job-specific input parameters. Shape varies per `agentType`. */
  context: z.record(z.unknown()),
  /**
   * Frozen copy of the active `agent_prompts` rows at dispatch time —
   * one entry per prompt key the agent uses (e.g. `autonomous` +
   * `targeted` for the curator, `default` for the validator). Same
   * reproducibility motivation as `config`: an in-flight run must
   * re-render its system prompt deterministically even if the prompt is
   * edited mid-run via the admin-ui Prompts tab. The orchestrator
   * populates this from `jobs.prompts_snapshot`, which is frozen
   * alongside `config_snapshot` in `createJobAndDispatch`.
   *
   * Empty (`{}`) only when dispatch ran without seeded prompts — agents
   * MUST throw if they need a key that isn't present, never fall back
   * to a baked-in default. See `docs/agent-prompts.md`.
   *
   * Defaulted via Zod so legacy `worker_result` blobs persisted before
   * this field existed still parse cleanly through `safeParse` in the
   * dispatch-runner's `recoverOutputFromDb` path (same pattern as
   * `expectsValidator` below).
   */
  prompts: z
    .record(
      z.object({
        version: z.number().int().positive(),
        content: z.string().min(1),
      }),
    )
    .default({}),
  /** Absolute URL the agent POSTs its `AgentOutput` to when done. */
  callbackUrl: z.string().url(),
  /**
   * Wall-clock budget for the agent run, in seconds. Mirrors
   * `agents.timeout_seconds`; agents that exceed it should fail fast and
   * report `status: "failure"` rather than letting the orchestrator's
   * dispatcher abort mid-flight.
   */
  timeoutSeconds: z.number().int().positive(),
});

export type AgentInput = z.infer<typeof agentInputSchema>;

// ─── Output (agent → orchestrator) ─────────────────────────────────────────

export const agentErrorSchema = z.object({
  /** Stable code for matching/aggregation. Free text — not enumerated. */
  code: z.string().min(1),
  /** Human-readable description for trace viewer / logs. */
  message: z.string().min(1),
  /** Optional structured detail. Not surfaced in user-facing UI. */
  details: z.unknown().optional(),
});

export type AgentError = z.infer<typeof agentErrorSchema>;

export const callSummarySchema = z.object({
  /** 0-based sequence within the job — matches `run_log.call_index`. */
  callIndex: z.number().int().nonnegative(),
  modelId: z.string().min(1),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  latencyMs: z.number().int().nonnegative(),
});

/**
 * Per-call summary the agent reports back. **Not** a `run_log` row — that
 * table is owned by `packages/messaging/` (CLAUDE.md invariant #7) and the
 * agent has already written it directly. The summary lives in
 * `audit_log.payload` so the trace viewer can see what an agent did without
 * cross-joining `run_log`.
 */
export type CallSummary = z.infer<typeof callSummarySchema>;

export const agentOutputSchema = z.object({
  /** Echoed from the input — must match the URL path's `:jobId`. */
  jobId: z.string().uuid(),
  /**
   * The agent's outcome. The orchestrator maps this to a job-state
   * transition (`success` → `validating` for worker agents,
   * `committing → completed` for validators; `failure` → `failed`).
   */
  status: z.enum(["success", "failure"]),
  /** Agent-produced artifacts (curator drafts, validator scores, etc.). */
  artifacts: z.array(z.unknown()),
  /** Errors encountered during the run. Empty for a clean success. */
  errors: z.array(agentErrorSchema),
  /**
   * Per-call summary — see {@link callSummarySchema}. Renamed from the
   * issue spec's `run_log` to avoid confusion with the `run_log` table,
   * which is owned by `@prospect/messaging` and written by the agent
   * itself (CLAUDE.md invariant #7).
   */
  callSummary: z.array(callSummarySchema),
  /** Wall-clock duration of the agent run, seconds. */
  durationSeconds: z.number().nonnegative(),
  /**
   * Whether the worker expects the orchestrator to dispatch a paired
   * validator after a successful run. Only consulted on **worker**
   * callbacks (the validator-hop gate in
   * `services/orchestrator/src/routes/callbacks.ts:nextStateFor`); the
   * orchestrator ignores it on validator callbacks because there is no
   * `_validator_validator` to chase.
   *
   * - `true` (default) — worker success transitions `running → validating`
   *   and the orchestrator dispatches the paired
   *   `${agentType}_validator`. Existing autonomous-curator behaviour.
   * - `false` — worker success transitions `running → completed`
   *   directly. The agent's drafts (if any) stay where they are; the
   *   operator can later dispatch a standalone validator from admin-ui
   *   via `POST /api/jobs`. Used by targeted-mode Curator runs (issue
   *   #212), where the operator wants a quick lookup without the
   *   automatic scoring chain.
   *
   * Defaulted via Zod so legacy `worker_result` blobs persisted before
   * this field existed still parse cleanly through `safeParse` in the
   * dispatch-runner's `recoverOutputFromDb` path
   * (`services/orchestrator/src/dispatch-runner.ts`) — they recover as
   * `true`, matching their original behaviour. Production construction
   * sites in `services/curator/src/run.ts`,
   * `services/validator/src/run.ts`, and the orchestrator's
   * `fullResult` write in
   * `services/orchestrator/src/routes/callbacks.ts` all set the field
   * explicitly; the Zod default only kicks in for legacy blobs and
   * legacy test fixtures.
   */
  expectsValidator: z.boolean().default(true),
});

export type AgentOutput = z.infer<typeof agentOutputSchema>;
